import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { WEB_MAX_CONTENT_WIDTH } from '../../theme/webLayoutWidths';
import { Platform, StyleSheet, View, ActivityIndicator } from 'react-native';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import MaterialIcons from '@react-native-vector-icons/material-icons/static';
import Ionicons from '@react-native-vector-icons/ionicons/static';
import {
  useTheme,
  useThemeColors,
  GTFonts,
  palette,
  useLayoutSize,
} from '../../theme';
import { s, fs, iconSize, v } from '../../utils/libs';
import BackOfficeFallback from '../../components/Reusables/BackOfficeFallback';
import useBackofficeOwnerAuth from '../../hooks/useBackofficeOwnerAuth';
import { HeaderLeft } from '../../components/Reusables/headers/HeaderLeft';
import BackOfficeHeaderLocationPicker from '../../components/Reusables/headers/BackOfficeHeaderLocationPicker';
import BackOfficeDashboardScreen from '../../screens/backoffice/BackOfficeDashboardScreen';
import BackOfficeProductsScreen from '../../screens/backoffice/BackOfficeProductsScreen';
import BackOfficeSalesScreen from '../../screens/backoffice/BackOfficeSalesScreen';
import BackOfficeExpensesScreen from '../../screens/backoffice/BackOfficeExpensesScreen';
import BackOfficeSuppliersScreen from '../../screens/backoffice/BackOfficeSuppliersScreen';
import BackOfficePeopleTabNavigator from './BackOfficePeopleTabNavigator';
import BackOfficeReportsScreen from '../../screens/backoffice/BackOfficeReportsScreen';
import BackOfficeIntegrationsScreen from '../../screens/backoffice/BackOfficeIntegrationsScreen';
import BackOfficeAccountScreen from '../../screens/backoffice/BackOfficeAccountScreen';
import BackOfficeOrdersTabNavigator from './BackOfficeOrdersTabNavigator';
import BackOfficeSidebarLayout from '../../components/web/BackOfficeSidebarLayout';
import WebHeaderBarInjector from '../../components/web/WebHeaderBarInjector';
import DateRangeDropdown from '../../components/Reusables/headers/DateRangeDropdown';
import HeaderActions from '../../components/Reusables/headers/HeaderActions';
import { isEmployeeAdminRole } from '../../utils/roles';
import { RootState } from '../../redux/types';
import BackOfficeExpandableTabBar from './BackOfficeExpandableTabBar';
import BackOfficePosFab from '../../components/backoffice/BackOfficePosFab';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { useDispatch } from 'react-redux';
import { dispatchOwnerActivationEvent } from '../../services/ownerActivationEvents';
import { useAppSelector } from '../../redux/helper';
import { selectValidCurrentLocation } from '../../redux/locationSlice';
import AntDesign from '@react-native-vector-icons/ant-design/static';
const Tab = createBottomTabNavigator();

/**
 * Dispatches BACKOFFICE_VIEWED only after the authenticated Back Office tree
 * successfully focuses/renders. Must not mount during autoLogin / loader.
 * Re-fires when locationId changes to credit the new location's bucket.
 * No-op on test or missing location.
 */
const BackOfficeActivationFocusReporter: React.FC = () => {
  const dispatch = useDispatch();
  const currentLocation = useAppSelector(selectValidCurrentLocation);
  const locationId = currentLocation?._id ?? null;
  const isTest = currentLocation?.bp_location_is_system_generated === true;
  const reportedLocationRef = useRef<string | null>(null);

  useFocusEffect(
    useCallback(() => {
      // No-op on test or missing location
      if (!locationId || isTest) return;
      // Only report once per location
      if (reportedLocationRef.current === locationId) return;
      reportedLocationRef.current = locationId;
      dispatchOwnerActivationEvent(dispatch, {
        type: 'BACKOFFICE_VIEWED',
        occurredAt: new Date().toISOString(),
        source: 'backoffice_focus',
        routeName: 'Dashboard',
        locationId,
        locationType: 'real',
      });
    }, [dispatch, locationId, isTest]),
  );

  // Re-fire when locationId changes while mounted (not just on focus)
  useEffect(() => {
    if (!locationId || isTest) return;
    if (reportedLocationRef.current === locationId) return;
    reportedLocationRef.current = locationId;
    dispatchOwnerActivationEvent(dispatch, {
      type: 'BACKOFFICE_VIEWED',
      occurredAt: new Date().toISOString(),
      source: 'backoffice_focus',
      routeName: 'Dashboard',
      locationId,
      locationType: 'real',
    });
  }, [dispatch, locationId, isTest]);

  return null;
};

function BackOfficeWebHeader({
  routeName,
  rightSlot,
}: {
  routeName: string;
  rightSlot?: React.ReactNode;
}) {
  const tabNavigation = useNavigation();
  return (
    <WebHeaderBarInjector
      title={<BackOfficeHeaderLocationPicker />}
      titleAlign="center"
      rightSlot={rightSlot}
      onBack={() => {
        if (routeName !== 'Dashboard') {
          tabNavigation.navigate('Dashboard' as never);
          return;
        }
        const parentNav = tabNavigation.getParent();
        if (parentNav?.canGoBack()) {
          parentNav.goBack();
          return;
        }
        try {
          (parentNav as any)?.popTo?.('DrawerNavigation');
        } catch {
          (parentNav as any)?.navigate?.('DrawerNavigation');
        }
      }}
    />
  );
}

const BackOfficeDashboardTab: React.FC = () => (
  <BackOfficeSidebarLayout>
    <BackOfficeWebHeader
      routeName="Dashboard"
      rightSlot={
        <DateRangeDropdown
          variant="compact"
          chipBgColor={'rgba(255,255,255,0.15)'}
          chipTextColor={'#fff'}
          chipBorderColor={'rgba(255,255,255,0.35)'}
        />
      }
    />
    <BackOfficeDashboardScreen />
  </BackOfficeSidebarLayout>
);

const BackOfficeInventoryTab: React.FC = () => (
  <BackOfficeSidebarLayout>
    {/* <BackOfficeWebHeader routeName="Inventory" /> */}
    <BackOfficeProductsScreen />
  </BackOfficeSidebarLayout>
);

const BackOfficeOrdersTab: React.FC = () => (
  <BackOfficeSidebarLayout>
    <BackOfficeOrdersTabNavigator />
  </BackOfficeSidebarLayout>
);

const BackOfficeSalesTab: React.FC = () => (
  <BackOfficeSidebarLayout>
    <BackOfficeWebHeader routeName="Sales" />
    <BackOfficeSalesScreen />
  </BackOfficeSidebarLayout>
);

const BackOfficeExpensesTab: React.FC = () => (
  <BackOfficeSidebarLayout>
    <BackOfficeWebHeader routeName="Expenses" />
    <BackOfficeExpensesScreen />
  </BackOfficeSidebarLayout>
);

const BackOfficeSuppliersTab: React.FC = () => (
  <BackOfficeSidebarLayout>
    {/* <BackOfficeWebHeader routeName="Suppliers" /> */}
    <BackOfficeSuppliersScreen />
  </BackOfficeSidebarLayout>
);

const BackOfficePeopleTab: React.FC = () => (
  <BackOfficeSidebarLayout>
    {/* Header owned by Staff/Customers screens (search + Add CTA), same as Suppliers */}
    <BackOfficePeopleTabNavigator />
  </BackOfficeSidebarLayout>
);

/** Report Settings cog on the Reports header (native and web sidebar), admins only. */
function ReportsHeaderActions() {
  const navigation = useNavigation();
  const employee = useAppSelector(
    (state: RootState) => state.auth.inApp.account.employeeInfo,
  );
  return (
    <HeaderActions
      actions={[
        {
          type: 'icon',
          icon: 'settings-outline',
          iconLibrary: 'ionicons',
          onPress: () => navigation.navigate('ReportSettingsScreen' as never),
          accessibilityLabel: 'Report settings',
          visible: isEmployeeAdminRole(employee?.bp_employee_role),
        },
      ]}
    />
  );
}

const BackOfficeReportsTab: React.FC = () => (
  <BackOfficeSidebarLayout>
    <BackOfficeWebHeader
      routeName="Reports"
      rightSlot={<ReportsHeaderActions />}
    />
    <BackOfficeReportsScreen />
  </BackOfficeSidebarLayout>
);

const BackOfficeIntegrationsTab: React.FC = () => (
  <BackOfficeSidebarLayout>
    <BackOfficeWebHeader routeName="Integrations" />
    <BackOfficeIntegrationsScreen />
  </BackOfficeSidebarLayout>
);

const BackOfficeAccountTab: React.FC = () => (
  <BackOfficeSidebarLayout>
    <BackOfficeWebHeader routeName="Account" />
    <BackOfficeAccountScreen />
  </BackOfficeSidebarLayout>
);

const BackOfficeTabNavigator: React.FC = () => {
  const { theme } = useTheme();
  const colors = useThemeColors();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const { owner, autoLoginOwner } = useBackofficeOwnerAuth();
  const { useSidebarNav } = useLayoutSize();

  if (!owner.accessToken) {
    return (
      <View style={styles.container}>
        {owner.error ? (
          <BackOfficeFallback
            title={'Back Office login required'}
            message={owner.error}
            primaryActionLabel={'Retry Login'}
            onPrimaryAction={() => {
              void autoLoginOwner();
            }}
          />
        ) : (
          <View style={styles.loaderWrap}>
            <ActivityIndicator color={theme.colors.successEmerald} />
          </View>
        )}
      </View>
    );
  }

  return (
    <View style={styles.root}>
      <BackOfficeActivationFocusReporter />
      <Tab.Navigator
      initialRouteName="Dashboard"
      tabBar={
        useSidebarNav
          ? () => null
          : (props: any) => (
              <BackOfficeExpandableTabBar
                {...props}
                firstRowSlots={6}
                expandedColumns={6}
                autoShrinkOnTabPress={false}
              />
            )
      }
      screenOptions={({ navigation: tabNavigation, route }) => ({
        headerShown: !useSidebarNav,
        headerStyle: { backgroundColor: theme.colors.primaryDark },
        headerTintColor: '#fff',
        headerTitleAlign: Platform.OS === 'web' ? 'center' : 'left',
        headerTitleContainerStyle:
          Platform.OS === 'web'
            ? { flex: 1, alignItems: 'center' as const }
            : undefined,
        headerTitle: () => (
          <BackOfficeHeaderLocationPicker
            containerStyle={
              Platform.OS === 'web' ? undefined : { marginLeft: s(10) }
            }
          />
        ),
        headerLeft: props => (
          <HeaderLeft
            {...props}
            padding={s(2)}
            onPress={() => {
              if (route.name !== 'Dashboard') {
                tabNavigation.navigate('Dashboard' as never);
                return;
              }
              const parentNav = tabNavigation.getParent();
              if (parentNav?.canGoBack()) {
                parentNav.goBack();
                return;
              }
              try {
                (parentNav as any)?.popTo?.('DrawerNavigation');
              } catch {
                (parentNav as any)?.navigate?.('DrawerNavigation');
              }
            }}
          />
        ),
        tabBarActiveTintColor: colors.primary, // #0088FE - shared with employee active
        tabBarInactiveTintColor: theme.colors.textGray500,
        tabBarLabelStyle: {
          fontFamily: GTFonts.regular,
          fontSize: fs(12),
          lineHeight: fs(16),
        },
      })}>
      <Tab.Screen
        name="Dashboard"
        options={{
          headerRight: () => (
            <View style={{ marginRight: s(8) }}>
              <DateRangeDropdown
                variant="compact"
                chipBgColor={'rgba(255,255,255,0.15)'}
                chipTextColor={'#fff'}
                chipBorderColor={'rgba(255,255,255,0.35)'}
              />
            </View>
          ),
          tabBarLabel: 'Dashboard',
          tabBarIcon: ({ color }) => (
            <AntDesign
              name="appstore"
              size={iconSize(26)}
              color={color}
            />
          ),
        }}
        component={BackOfficeDashboardTab}
      />
      <Tab.Screen
        name="People"
        options={{
          tabBarLabel: 'People',
          tabBarIcon: ({ color }) => (
            <MaterialCommunityIcons
              name="account-group"
              size={iconSize(26)}
              color={color}
            />
          ),
        }}
        component={BackOfficePeopleTab}
      />
      <Tab.Screen
        name="Inventory"
        options={{
          tabBarIcon: ({ color }) => (
            <MaterialIcons
              name="inventory"
              size={iconSize(24)}
              color={color}
            />
          ),
        }}
        component={BackOfficeInventoryTab}
      />
      <Tab.Screen
        name="Orders"
        options={{
          tabBarIcon: ({ color }) => (
            <Ionicons name="receipt-sharp" size={iconSize(22)} color={color} />
          ),
        }}
        component={BackOfficeOrdersTab}
      />
      <Tab.Screen
        name="Sales"
        options={{
          tabBarIcon: ({ color }) => (
            <MaterialCommunityIcons
              name="chart-areaspline-variant"
              size={iconSize(24)}
              color={color}
            />
          ),
        }}
        component={BackOfficeSalesTab}
      />
      <Tab.Screen
        name="Suppliers"
        options={{
          tabBarIcon: ({ color }) => (
            <MaterialCommunityIcons
              name="truck-delivery"
              size={iconSize(24)}
              color={color}
            />
          ),
        }}
        component={BackOfficeSuppliersTab}
      />
      <Tab.Screen
        name="Expenses"
        options={{
          tabBarIcon: ({ color }) => (
            <MaterialCommunityIcons
              name="cash-plus"
              size={iconSize(24)}
              color={color}
            />
          ),
        }}
        component={BackOfficeExpensesTab}
      />
      <Tab.Screen
        name="Reports"
        options={{
          headerRight: () => (
            <View style={{ marginRight: s(8) }}>
              <ReportsHeaderActions />
            </View>
          ),
          tabBarIcon: ({ color }) => (
            <MaterialCommunityIcons
              name="file-chart"
              size={iconSize(24)}
              color={color}
            />
          ),
        }}
        component={BackOfficeReportsTab}
      />
      <Tab.Screen
        name="Integrations"
        options={{
          tabBarIcon: ({ color }) => (
            <MaterialCommunityIcons
              name="puzzle"
              size={iconSize(24)}
              color={color}
            />
          ),
        }}
        component={BackOfficeIntegrationsTab}
      />
      <Tab.Screen
        name="Account"
        options={{
          tabBarLabel: 'Account',
          tabBarIcon: ({ color }) => (
            <MaterialCommunityIcons
              name="account-circle"
              size={iconSize(24)}
              color={color}
            />
          ),
        }}
        component={BackOfficeAccountTab}
      />
    </Tab.Navigator>
      <BackOfficePosFab />
    </View>
  );
};

export default BackOfficeTabNavigator;

const createStyles = (theme: any) =>
  StyleSheet.create({
    root: {
      flex: 1,
    },
    container: {
      flex: 1,
      backgroundColor: theme.colors.background,
      paddingHorizontal: s(5),
      paddingTop: v(30),
      ...(Platform.OS === 'web' && {
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center',
        width: '100%',
        paddingHorizontal: s(24),
      }),
    },
    loaderWrap: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
    },
  });
